/**
 * Chart data for the COD reports — pure functions over the same (already filtered) data the
 * report lists use, so every chart follows the date range, search and toggles.
 */
import { CodOrder, CodPaymentTransaction } from "../types/codSalesRep";
import { computeItemTotal, getOrderPayableTotal } from "./codUtils";
import { CustomerReportRow, orderBucket, orderDate, RepReportRow } from "./codReports";

export interface ChartDatum {
  key: string;
  label: string;
  value: number;
  /** Optional secondary text (e.g. customer code). */
  sub?: string;
}

export interface MonthDatum {
  key: string;
  label: string;
  value: number;
}

const monthKey = (value?: string) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
};

const monthLabel = (key: string) => {
  const [y, m] = key.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("en-US", { month: "short", year: "2-digit" });
};

/** Continuous run of months (no gaps) covering the given keys, capped to the latest `max`. */
const monthRun = (keys: string[], max = 12): string[] => {
  const valid = keys.filter(Boolean).sort();
  if (valid.length === 0) return [];
  const [fy, fm] = valid[0].split("-").map(Number);
  const [ly, lm] = valid[valid.length - 1].split("-").map(Number);
  const run: string[] = [];
  let y = fy;
  let m = fm;
  while (y < ly || (y === ly && m <= lm)) {
    run.push(`${y}-${String(m).padStart(2, "0")}`);
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return run.slice(-max);
};

/** Generic: sum `value(item)` per month of `date(item)`, as a continuous run of months. */
export const byMonth = <T,>(
  items: T[],
  date: (item: T) => string | undefined,
  value: (item: T) => number,
  max = 12
): MonthDatum[] => {
  const totals = new Map<string, number>();
  items.forEach((item) => {
    const key = monthKey(date(item));
    if (key) totals.set(key, (totals.get(key) ?? 0) + value(item));
  });
  return monthRun(Array.from(totals.keys()), max).map((key) => ({
    key,
    label: monthLabel(key),
    value: totals.get(key) ?? 0,
  }));
};

/** Generic: top N of a keyed tally, rest folded into "Other". */
export const topTally = (
  entries: { key: string; label: string; sub?: string; value: number }[],
  n: number,
  otherLabel: string
): ChartDatum[] => topWithOther(entries, n, otherLabel);

/** Approved-order value per month (by order date). */
export const salesByMonth = (orders: CodOrder[], max = 12): MonthDatum[] => {
  const totals = new Map<string, number>();
  orders
    .filter((order) => orderBucket(order) === "approved")
    .forEach((order) => {
      const key = monthKey(orderDate(order));
      if (key) totals.set(key, (totals.get(key) ?? 0) + getOrderPayableTotal(order));
    });
  return monthRun(Array.from(totals.keys()), max).map((key) => ({
    key,
    label: monthLabel(key),
    value: totals.get(key) ?? 0,
  }));
};

const isCountablePayment = (payment: CodPaymentTransaction) =>
  !/fail|cancel|reject|void/i.test(payment.status ?? "");

/** Cash collected per month (by payment date). */
export const collectionsByMonth = (payments: CodPaymentTransaction[], max = 12): MonthDatum[] => {
  const totals = new Map<string, number>();
  payments.filter(isCountablePayment).forEach((payment) => {
    const key = monthKey(payment.paymentDate);
    if (key) totals.set(key, (totals.get(key) ?? 0) + Number(payment.amount || 0));
  });
  return monthRun(Array.from(totals.keys()), max).map((key) => ({
    key,
    label: monthLabel(key),
    value: totals.get(key) ?? 0,
  }));
};

/** Top N by value; the rest folds into a single "Other" bar (never a generated extra colour). */
const topWithOther = (items: ChartDatum[], n: number, otherLabel: string): ChartDatum[] => {
  const sorted = items.filter((item) => item.value > 0).sort((a, b) => b.value - a.value);
  if (sorted.length <= n) return sorted;
  const head = sorted.slice(0, n);
  const rest = sorted.slice(n).reduce((sum, item) => sum + item.value, 0);
  return [...head, { key: "__other", label: `${otherLabel} (${sorted.length - n})`, value: rest }];
};

export const topCustomers = (rows: CustomerReportRow[], n: number, otherLabel: string) =>
  topWithOther(
    rows.map((row) => ({
      key: row.key,
      label: row.customerName || row.customerCode || "—",
      sub: row.customerCode,
      value: row.approvedValue,
    })),
    n,
    otherLabel
  );

/** Customers across all reps (manager view), merged by customer key. */
export const topCustomersAcrossReps = (reps: RepReportRow[], n: number, otherLabel: string) => {
  const merged = new Map<string, ChartDatum>();
  reps.forEach((rep) =>
    rep.customers.forEach((row) => {
      const entry = merged.get(row.key) ?? {
        key: row.key,
        label: row.customerName || row.customerCode || "—",
        sub: row.customerCode,
        value: 0,
      };
      entry.value += row.approvedValue;
      merged.set(row.key, entry);
    })
  );
  return topWithOther(Array.from(merged.values()), n, otherLabel);
};

export const topReps = (reps: RepReportRow[], n: number, otherLabel: string) =>
  topWithOther(
    reps.map((rep) => ({ key: rep.key, label: rep.repName, sub: rep.repCode, value: rep.approvedValue })),
    n,
    otherLabel
  );

/** Best-selling products by value across approved orders. */
export const topProducts = (orders: CodOrder[], n: number, otherLabel: string) => {
  const totals = new Map<string, ChartDatum>();
  orders
    .filter((order) => orderBucket(order) === "approved")
    .forEach((order) =>
      (order.items ?? []).forEach((item) => {
        const key = (item.code || item.productId || item.name || "").toLowerCase();
        if (!key) return;
        const entry = totals.get(key) ?? { key, label: item.name || item.code || "—", sub: item.code, value: 0 };
        entry.value += computeItemTotal(item);
        totals.set(key, entry);
      })
    );
  return topWithOther(Array.from(totals.values()), n, otherLabel);
};
